import React, { useState, useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@material-ui/core/styles'

import { useSnackbar } from 'notistack'

import messages from '../../../services/messages'

import {
  deleteTab,
  setConversationTabs,
  setConversationTabsStatus,
} from '../../../redux/tabList/actions'

import { CircularProgress } from '@material-ui/core'

import {
  DeleteRounded as DeleteIcon,
  FindInPageRounded as InfoIcon,
} from '@material-ui/icons'

import DateTime from 'luxon/src/datetime.js'

import useStyles from './TabListStyle'
import IconButton from '../../atoms/inputs/iconButton'
import Typography from '../../atoms/display/typography'
import EmptyState from '../../atoms/display/emptyState'
import HighlightSearch from '../../atoms/display/highlightSearch'
import Alert from '../../molecules/feedback/dialogs/alert'

const TabList = (props) => {
  const { chatId } = props
  const { t } = useTranslation()
  const classes = useStyles()
  const theme = useTheme()
  const dispatch = useDispatch()
  const [tabId, setTabId] = useState('')
  const [openCloseAlert, setOpenCloseAlert] = useState(false)

  const tabList = useSelector((state) => state.tabList)
  const user = useSelector((state) => state.user)

  const { enqueueSnackbar } = useSnackbar()

  const loadtTabList = async (chatId) => {
    if (chatId && !tabList?.conversations?.[chatId]) {
      dispatch(setConversationTabsStatus('get_tabs', { loading: true }))
      try {
        const res = await messages.get(
          `/tech4h-messages/conversation/tabs/${chatId}`
        )
        if (res.status === 200) {
          const response = res.data.data ?? null
          dispatch(setConversationTabs(chatId, response))
        } else {
          throw res.data
        }
      } catch (error) {
        enqueueSnackbar(t('toasts.actionError'), {
          variant: 'error',
        })
      } finally {
        dispatch(setConversationTabsStatus('get_tabs', { loading: false }))
      }
    }
  }

  const handleDelete = async (chatId, tabId) => {
    if (chatId) {
      dispatch(setConversationTabsStatus('delete_tab', { loading: true }))
      try {
        const res = await messages.delete(
          `/tech4h-messages/conversation/tab/${chatId}`,
          { data: { tab_id: tabId } }
        )
        if (res.status === 200) {
          const response = tabId ?? null
          dispatch(deleteTab(chatId, response))
          setOpenCloseAlert(false)
        } else throw res.data
      } catch (error) {
        enqueueSnackbar(t('toasts.actionError'), {
          variant: 'error',
        })
      } finally {
        dispatch(setConversationTabsStatus('delete_tab', { loading: false }))
      }
    }
  }

  const openDeleteModal = (id) => {
    setTabId(id)
    setOpenCloseAlert(true)
  }

  const formatDate = (value) => {
    const date = new Date(value)
    const format = `D '-' t`
    const time = DateTime.fromJSDate(date).setLocale('pt-br').toFormat(format)
    return isNaN(date.getTime()) ? value : time
  }

  const buttonDelete = (id, userId) => {
    if (user?.id === userId) {
      return (
        <>
          <div style={{ width: 10 }} />
          <div className={classes.contentIcons}>
            <IconButton
              color="primary"
              size="small"
              onClick={(event) => {
                event.stopPropagation()
                openDeleteModal(id)
              }}
            >
              <DeleteIcon
                style={{
                  color: theme.palette.primary.main,
                  fontSize: 14,
                  margin: 3,
                }}
              />
            </IconButton>
          </div>
        </>
      )
    }
  }

  const renderUsername = (el) => {
    if (el?.username) {
      return (
        <>
          <HighlightSearch text={el.username} /> {'-'}
        </>
      )
    } else {
      return (
        <>
          <HighlightSearch text={' N/A '} /> {'-'}
        </>
      )
    }
  }

  const renderList = () => {
    if (
      tabList?.status?.get_tabs?.loading ||
      tabList?.status?.delete_tab?.loading
    ) {
      return (
        <div className={classes.loadingSection}>
          <CircularProgress size={25} color="primary" />
        </div>
      )
    }
    return (
      <>
        <div
          style={{
            marginTop: 15,
            marginBottom: 15,
          }}
        >
          {tabList?.conversations?.[chatId]?.length > 0 ? (
            tabList?.conversations[chatId]?.map((el) => (
              <div className={classes.tabContent}>
                <div>
                  <Typography
                    variant="h6"
                    style={{ color: theme.palette.primary.main }}
                  >
                    <HighlightSearch text={formatDate(el.date)} /> {'- '}
                    {renderUsername(el)}
                    <HighlightSearch text={el.skillName} />
                  </Typography>
                  <Typography variant="body1" className={classes.tabName}>
                    <HighlightSearch
                      text={
                        el.tabName === 'Closed System'
                          ? t('commons.closedSystem')
                          : el.tabName
                      }
                    />
                  </Typography>
                </div>
                {buttonDelete(el.tabId, el.userId)}
              </div>
            ))
          ) : (
            <EmptyState text={t('commons.searchResultEmpty')}>
              <InfoIcon />
            </EmptyState>
          )}
        </div>
        {openCloseAlert && (
          <Alert
            open={setOpenCloseAlert}
            setOpen={setOpenCloseAlert}
            title={t('commons.question')}
            text={t('alerts.removeTab')}
            agreeAction={() => handleDelete(chatId, tabId)}
            loading={tabList?.status?.delete_tab?.loading}
          />
        )}
      </>
    )
  }

  useEffect(() => {
    loadtTabList(chatId)
  }, [chatId])

  useEffect(() => {
    if (!openCloseAlert && tabId) setTabId(null)
  }, [openCloseAlert, tabId])

  return <>{renderList()}</>
}

export default TabList
